// Hand-minting, one drone at a time. SwarmSystems only.
//
// Swarm's D002 line has no flasher yet, so serials are allocated by hand: the
// operator is told the next serial and the Raspberry Pi hostname to set, goes and
// sets it, then confirms. Confirming is an assertion about the physical world
// ("I used this one"), which is why it mints rather than merely reserving.
//
// The hostname is the reason this page exists. It is DERIVED from the serial's
// last group server-side, never stored, so it cannot drift from the serial.
//
// Not to be confused with /serialgen, which mints a POOL of serials in bulk for
// the flasher to draw from. That one is on both instances; this one is not.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '../../apiHelpers';
import { useToast } from '../../components/Toast/ToastContext';
import {
  printStandardLabels,
  printSquareQRLabels,
  printBothLabelTypes,
} from '../../utils/printing-utils';
import PageLayout from '../../components/ui/PageLayout';
import { buttonClass } from '../../components/ui/Button';
import { Field } from '../../components/ui/Field';
import { Select } from '../../components/ui/Select';
import { Alert } from '../../components/ui/Alert';
import FadeIn from '../../components/ui/FadeIn';
import Reveal from '../../components/ui/Reveal';
import { EmptyState } from '../../components/ui/EmptyState';
import { ListRow, ListStack } from '../../components/ui/ListRow';
import { Panel } from '../../components/ui/Panel';
import { Skeleton, SkeletonGroup } from '../../components/ui/Skeleton';

interface BatchRow {
  batchserial: number;
}

interface Suggestion {
  serialnumber: string;
  hostname: string;
  dronehex: string;
  dronereadable: string;
}

interface MintResult {
  serial: { serialnumber: string; dronehex: string; dronereadable: string };
  hostname: string;
  superseded: { expected: string; actual: string } | null;
}

interface RangeSerial {
  serialnumber: string;
  dronehex: string;
  dronereadable: string;
}

const labelClass = 'block text-[11px] uppercase tracking-wider text-on-surface-variant mb-1';

const now = new Date();

const SerialMint: React.FC = () => {
  const api = useApi();
  const toast = useToast();

  const [modelserial, setModelserial] = useState('D002');
  const [batches, setBatches] = useState<BatchRow[]>([]);
  const [batchserial, setBatchserial] = useState('');
  const [month, setMonth] = useState(String(now.getMonth() + 1).padStart(2, '0'));
  const [year, setYear] = useState(String(now.getFullYear()).slice(-2));

  const [suggestion, setSuggestion] = useState<Suggestion | null>(null);
  const [loading, setLoading] = useState(false);
  const [minting, setMinting] = useState(false);
  const [session, setSession] = useState<MintResult[]>([]);
  const [copied, setCopied] = useState(false);

  const [rangeFrom, setRangeFrom] = useState('');
  const [rangeTo, setRangeTo] = useState('');
  const [rangeSerials, setRangeSerials] = useState<RangeSerial[]>([]);
  const [rangeLoading, setRangeLoading] = useState(false);

  useEffect(() => {
    api.get('/batchdetails', {
      onSuccess: (rows: BatchRow[]) => {
        setBatches(rows);
        if (rows.length) {
          // Matches SerialNumberGenerator: batchdetails.batchserial is an INTEGER
          // and the serial carries 4 hex chars, so this pads rather than converts.
          // Identical for batches 0-9; batch 10 would render "0010" (hex 16). Not
          // reachable while Swarm is on batch 1, but do not copy this into a third
          // place without fixing it in all of them.
          const highest = [...rows].sort((a, b) => b.batchserial - a.batchserial)[0];
          setBatchserial(String(highest.batchserial).padStart(4, '0'));
        }
      },
      errorAlert: 'Could not load batches',
    });
    // api excluded deliberately: it is recreated each render and would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const params = { modelserial, batchserial, month, year };
  const ready = Boolean(modelserial && batchserial && month && year);

  const loadNext = useCallback(async () => {
    if (!ready) return;
    setLoading(true);
    setCopied(false);
    const query = new URLSearchParams(params).toString();
    await api.get(`/serialnumbers/next?${query}`, {
      onSuccess: (data: Suggestion) => setSuggestion(data),
      errorAlert: 'Could not work out the next serial',
    });
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelserial, batchserial, month, year, ready]);

  // Invalidates in-flight preprint loads. A response that started under old
  // params must never land: the serials it carries embed the OLD
  // model/date/batch, and the screen would show the NEW ones over them —
  // up to 1000 physical stickers wrong with no on-screen evidence.
  const rangeRequestRef = useRef(0);

  useEffect(() => {
    setSuggestion(null);
    // Loaded preprint serials embed the OLD model/date/batch, so a param change
    // makes them stale in a way the screen would not show. Drop them, and
    // orphan any load still in flight.
    rangeRequestRef.current += 1;
    setRangeSerials([]);
    loadNext();
  }, [loadNext]);

  const loadRange = async () => {
    const from = parseInt(rangeFrom, 10);
    const to = parseInt(rangeTo, 10);
    if (!Number.isInteger(from) || !Number.isInteger(to) || from < 1 || to < from) {
      toast.error('Enter a range of drone numbers, low to high.');
      return;
    }
    if (to - from + 1 > 1000) {
      toast.error('1000 labels is the most one run can print.');
      return;
    }
    const requestId = ++rangeRequestRef.current;
    setRangeLoading(true);
    const query = new URLSearchParams({ ...params, from: String(from), to: String(to) }).toString();
    try {
      await api.get(`/serialnumbers/preview-range?${query}`, {
        onSuccess: (data: { serials: RangeSerial[] }) => {
          if (requestId === rangeRequestRef.current) setRangeSerials(data.serials);
        },
        errorAlert: 'Could not build serials for that range',
      });
    } catch {
      // errorAlert already told the operator; the finally keeps Load usable.
    } finally {
      setRangeLoading(false);
    }
  };

  const printRange = async (mode: 'labels' | 'qr' | 'both') => {
    if (!rangeSerials.length) return;
    if (mode === 'labels') await printStandardLabels(rangeSerials);
    else if (mode === 'qr') await printSquareQRLabels(rangeSerials);
    else await printBothLabelTypes(rangeSerials);
  };

  const mint = async () => {
    if (!suggestion || minting) return;
    setMinting(true);
    await api.post('/serialnumbers/mint', {
      body: { ...params, expected_serialnumber: suggestion.serialnumber },
      onSuccess: (data: MintResult) => {
        setSession((prev) => [data, ...prev]);
        if (data.superseded) {
          toast.error(
            `Someone else took ${data.superseded.expected}. This drone is ${data.superseded.actual}, so set the Pi to ${data.hostname}.`
          );
        } else {
          toast.success(`Minted ${data.serial.serialnumber}`);
        }
      },
      errorAlert: 'Mint failed. Nothing was created.',
    });
    setMinting(false);
    loadNext();
  };

  const copyHostname = async () => {
    if (!suggestion) return;
    try {
      await navigator.clipboard.writeText(suggestion.hostname);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy. Select the text instead.');
    }
  };

  return (
    <PageLayout kind="focus" className="pt-6 text-on-surface">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold">Mint Drone</h1>
        <p className="text-sm text-on-surface-variant mt-1">
          Take the next serial, set that hostname on the Raspberry Pi, then confirm. Confirming
          creates the drone and moves the counter on.
        </p>
      </header>

      <section className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div>
          <label className={labelClass} htmlFor="mint-model">Model</label>
          <Field
            id="mint-model"
            value={modelserial}
            maxLength={4}
            onChange={(e) => setModelserial(e.target.value.toUpperCase())}
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="mint-batch">Batch</label>
          <Select
            id="mint-batch"
            label="Batch"
            value={batchserial || null}
            placeholder={batches.length ? 'Select a batch' : 'No batches yet'}
            disabled={!batches.length}
            onChange={setBatchserial}
            options={batches.map((b) => ({ key: String(b.batchserial).padStart(4, '0'), label: `Batch ${b.batchserial}` }))}
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="mint-month">Month</label>
          <Field
            id="mint-month"
            value={month}
            maxLength={2}
            onChange={(e) => setMonth(e.target.value)}
          />
        </div>
        <div>
          <label className={labelClass} htmlFor="mint-year">Year</label>
          <Field
            id="mint-year"
            value={year}
            maxLength={2}
            onChange={(e) => setYear(e.target.value)}
          />
        </div>
      </section>

      <Reveal show={!batches.length}>
        <Alert tone="warning" className="mb-6">
          No batches exist on this instance yet. A batch has to be created before anything can be
          minted.
        </Alert>
      </Reveal>

      <Panel title="Next up" titleStyle="section" className="mb-8">
        <FadeIn key={loading ? 'loading' : suggestion ? 'suggestion' : 'none'}>
        {loading && (
          <SkeletonGroup label="Loading the next serial" className="space-y-3 py-2">
            <Skeleton className="h-9 w-2/3" />
            <Skeleton className="h-5 w-full" />
          </SkeletonGroup>
        )}

        {!loading && !suggestion && (
          <EmptyState variant="area">Nothing to suggest. Check the fields above.</EmptyState>
        )}

        {!loading && suggestion && (
          <div>
            <div className="mb-5">
              <div className={labelClass}>Set this hostname on the Pi</div>
              <div className="flex items-center gap-3 flex-wrap">
                <code className="text-3xl font-mono text-primary break-all">{suggestion.hostname}</code>
                <button
                  type="button"
                  onClick={copyHostname}
                  className={buttonClass('quiet', 'sm')}
                >
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>

            <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 text-sm">
              <div>
                <dt className={labelClass}>Serial</dt>
                <dd className="font-mono break-all">{suggestion.serialnumber}</dd>
              </div>
              <div>
                <dt className={labelClass}>Last four</dt>
                <dd className="font-mono">{suggestion.dronehex}</dd>
              </div>
              <div>
                <dt className={labelClass}>Drone number</dt>
                <dd className="font-mono">{suggestion.dronereadable}</dd>
              </div>
            </dl>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={mint}
                disabled={minting}
                className={buttonClass('filled', 'md')}
              >
                {minting ? 'Minting...' : 'I set this hostname, mint it'}
              </button>
              <button
                type="button"
                onClick={loadNext}
                disabled={minting}
                className={buttonClass('quiet', 'md')}
              >
                Refresh
              </button>
            </div>
          </div>
        )}
        </FadeIn>
      </Panel>

      <Reveal show={session.length > 0}>
        <Panel title={`Minted this session (${session.length})`} titleStyle="section" padding="sm">
          <ListStack
            items={session}
            getKey={(m) => m.serial.serialnumber}
            on="panel"
            label="Minted this session"
            renderRow={(m) => (
              <ListRow>
                <span className="flex items-baseline gap-4 text-sm">
                  {/* The drone number is the only part of a serial that reads in
                      order. dronehex is a digit reversal, so #10 (0064) sorts
                      nowhere near #9 (2328) and the list would look shuffled. */}
                  <span className="font-mono text-on-surface-variant tabular-nums shrink-0 w-14">
                    #{m.serial.dronereadable}
                  </span>
                  <span className="font-mono text-on-surface break-all flex-1 min-w-0">
                    {m.serial.serialnumber}
                  </span>
                  <span className="font-mono text-primary whitespace-nowrap">{m.hostname}</span>
                </span>
              </ListRow>
            )}
          />
        </Panel>
      </Reveal>

      <Panel title="Preprint labels" titleStyle="section" className="mt-8">
        <div>
          <p className="text-sm text-on-surface-variant mb-4">
            Print stickers for a run of drone numbers before the drones exist, using the model,
            batch, and date above. Nothing is minted or reserved: the labels only match if the
            drones are later minted under that same combination.
          </p>

          <div className="flex items-end gap-3 flex-wrap">
            <div className="w-28">
              <label className={labelClass} htmlFor="preprint-from">From drone #</label>
              <Field
                id="preprint-from"
                    inputMode="numeric"
                value={rangeFrom}
                onChange={(e) => setRangeFrom(e.target.value)}
                placeholder="100"
              />
            </div>
            <div className="w-28">
              <label className={labelClass} htmlFor="preprint-to">To drone #</label>
              <Field
                id="preprint-to"
                    inputMode="numeric"
                value={rangeTo}
                onChange={(e) => setRangeTo(e.target.value)}
                placeholder="150"
              />
            </div>
            <button
              type="button"
              onClick={loadRange}
              disabled={rangeLoading || !ready}
              className={buttonClass('quiet', 'md')}
            >
              {rangeLoading ? 'Loading...' : 'Load'}
            </button>
          </div>

          <Reveal show={rangeSerials.length > 0}>
            <div className="mt-5 space-y-3">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-sm">
                  <span className="text-on-surface font-medium">{rangeSerials.length} labels</span>{' '}
                  <span className="text-on-surface-variant">
                    drone #{rangeSerials[0]?.dronereadable} to #
                    {rangeSerials[rangeSerials.length - 1]?.dronereadable}
                  </span>
                </div>
                {/* Same three print modes and colors as Firefleet's Batch Label Print,
                    through the same printing-utils, so the shop learns one legend. */}
                <div className="flex gap-2">
                  <button
                    type="button"
                    className={buttonClass('filled')}
                    onClick={() => printRange('labels')}
                  >
                    Labels
                  </button>
                  <button
                    type="button"
                    className={buttonClass('info')}
                    onClick={() => printRange('qr')}
                  >
                    QR
                  </button>
                  <button
                    type="button"
                    className={buttonClass('good')}
                    onClick={() => printRange('both')}
                  >
                    Both
                  </button>
                </div>
              </div>

              <details className="border border-gray-800 rounded">
                <summary className="px-3 py-2 cursor-pointer text-sm text-on-surface-variant hover:text-on-surface">
                  View serial numbers
                </summary>
                <div className="p-3 max-h-48 overflow-y-auto border-t border-gray-800">
                  <ul className="divide-y divide-gray-800">
                    {rangeSerials.map((s) => (
                      <li key={s.serialnumber} className="px-1 py-1.5 flex items-baseline gap-4 text-sm">
                        <span className="font-mono text-on-surface-variant tabular-nums shrink-0 w-14">
                          #{s.dronereadable}
                        </span>
                        <span className="font-mono text-on-surface break-all">{s.serialnumber}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </details>
            </div>
          </Reveal>
        </div>
      </Panel>
    </PageLayout>
  );
};

export default SerialMint;
